import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { useFieldControlProps } from './field';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: React.Ref<HTMLTextAreaElement>;
  /** Show "n / maxLength" under the text (401:8643). Requires `maxLength`, or pass `counter` explicitly. */
  showCounter?: boolean;
  /** Left caption of the counter row when empty (e.g. "Matn bo'sh"). */
  emptyHint?: React.ReactNode;
  invalid?: boolean;
  /** Allow vertical resizing. Default false (fixed `rows`). */
  resizable?: boolean;
  /** Class of the outer box. */
  className?: string;
  textareaClassName?: string;
}

/**
 * Product textarea (401:8641 "Matn maydoni"): White/5 box, 1px White/10 inside, r8, padding 10, gap 8; value 400 13/18,
 * placeholder White/40; counter 400 10/auto White/40. Focus: `#35b5ec` border.
 */
export function Textarea({
  ref,
  showCounter,
  emptyHint,
  invalid,
  resizable,
  className,
  textareaClassName,
  value,
  defaultValue,
  onChange,
  maxLength,
  rows = 4,
  id,
  disabled,
  required,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextareaProps) {
  const field = useFieldControlProps({ id, disabled, required, invalid, 'aria-describedby': ariaDescribedBy });
  const [uncontrolledLength, setUncontrolledLength] = useState(() => String(defaultValue ?? '').length);
  const length = value !== undefined ? String(value ?? '').length : uncontrolledLength;
  const counterId = field.id ? `${field.id}-counter` : undefined;
  return (
    <div
      className={cx(
        'flex w-full min-w-0 flex-col gap-8 rounded-8 border border-line bg-subtle p-10 transition-colors duration-150',
        'focus-within:border-focus has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60',
        field.invalid && 'border-tone-red-50 bg-tone-red-5 focus-within:border-tone-red',
        className,
      )}
    >
      <textarea
        {...rest}
        ref={ref}
        id={field.id}
        rows={rows}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        disabled={field.disabled}
        required={field.required}
        aria-invalid={field.invalid || undefined}
        aria-describedby={[field['aria-describedby'], showCounter ? counterId : undefined].filter(Boolean).join(' ') || undefined}
        onChange={(e) => {
          if (value === undefined) setUncontrolledLength(e.currentTarget.value.length);
          onChange?.(e);
        }}
        className={cn(
          't-field min-h-36 w-full bg-transparent text-fg outline-none placeholder:text-fg-tertiary disabled:cursor-not-allowed',
          resizable ? 'resize-y' : 'resize-none',
          textareaClassName,
        )}
      />
      {showCounter && (
        <div id={counterId} className="flex items-center justify-between gap-8 text-10 text-fg-tertiary">
          <span>{length === 0 ? emptyHint : null}</span>
          <span className="tabular-nums" aria-live="polite">
            {maxLength != null ? `${length} / ${maxLength}` : length}
          </span>
        </div>
      )}
    </div>
  );
}
